<template>
  <div :class="[bem.b(), bem.m(props.theme), isFinish ? bem.is('finish') : '']">
    <!-- 礼花容器 -->
    <div ref="confettiContainer" :class="bem.e('confetti-container')"></div>

    <div v-if="!!props.title" :class="bem.e('title')" :style="props.titleStyle">
      {{ props.title }}
    </div>
    <div :class="bem.e('content')">
      <template v-for="(item, index) in displayItems" :key="item.key">
        <div :class="bem.e('item')">
          <span :class="bem.e('value')">{{ item.value }}</span>
          <span :class="bem.e('label')">{{ item.label }}</span>
        </div>
        <span
          v-if="index < displayItems.length - 1"
          :class="bem.e('separator')"
        >
          :
        </span>
      </template>
    </div>

    <!-- 控制按钮 -->
    <div v-if="props.control" :class="bem.e('controls')">
      <button :class="[bem.e('btn'), isRunning ? bem.em('btn', 'pause') : bem.em('btn', 'play')]" @click="togglePlayPause">
        <!-- 播放图标 -->
        <svg v-if="!isRunning" viewBox="0 0 24 24" fill="currentColor">
          <path d="M8 5v14l11-7z" />
        </svg>
        <!-- 暂停图标 -->
        <svg v-else viewBox="0 0 24 24" fill="currentColor">
          <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
        </svg>
      </button>
      <button :class="[bem.e('btn'), bem.em('btn', 'reset')]" @click="handleReset">
        <!-- 重置图标 -->
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z" />
        </svg>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { createNamespace } from '@ll-plus/utils'
import { countdownProps, countdownEmits } from './countdown'
import type { TimeUnit } from './countdown'

defineOptions({ name: 'LlCountdown' })

const props = defineProps(countdownProps)
const emits = defineEmits(countdownEmits)
const bem = createNamespace('countdown')

// 单位标签
const UNIT_LABELS: Record<TimeUnit, string> = {
  yyyy: '年',
  MM: '月',
  dd: '日',
  HH: '时',
  mm: '分',
  ss: '秒',
}

// 全量单位顺序（从大到小）
const ALL_UNITS: TimeUnit[] = ['yyyy', 'MM', 'dd', 'HH', 'mm', 'ss']

// 各单位换算为秒
const UNIT_SECONDS: Record<TimeUnit, number> = {
  yyyy: 365 * 24 * 60 * 60,
  MM: 30 * 24 * 60 * 60,
  dd: 24 * 60 * 60,
  HH: 60 * 60,
  mm: 60,
  ss: 1,
}

// 目标时间戳（ms）
const targetTime = ref<number>(0)
// 剩余时间（ms）
const remainingMs = ref<number>(0)
// 倒计时是否完成
const isFinish = ref(false)
// 是否正在运行
const isRunning = ref(false)
// 计时器
let timer: ReturnType<typeof setInterval> | null = null
// 完成态动画定时器
let finishTimer: ReturnType<typeof setTimeout> | null = null
// 礼花容器
const confettiContainer = ref<HTMLElement | null>(null)

// 计算目标时间戳：mode='timer' 时把 value 解析为目标时间，否则以 value 为时长从当前时刻起算
const resolveTargetTime = (): number => {
  if (props.mode === 'timer') {
    const v =
      props.value instanceof Date
        ? props.value
        : new Date(props.value as string | number)
    return v.getTime()
  }
  return Date.now() + Number(props.value)
}

const init = () => {
  if (finishTimer !== null) {
    clearTimeout(finishTimer)
    finishTimer = null
  }
  targetTime.value = resolveTargetTime()
  remainingMs.value = Math.max(0, targetTime.value - Date.now())
  isFinish.value = false
}

const tick = () => {
  const delta = targetTime.value - Date.now()
  if (delta <= 0) {
    remainingMs.value = 0
    stopTimer()
    isFinish.value = true

    if (finishTimer !== null) {
      clearTimeout(finishTimer)
    }

    finishTimer = setTimeout(() => {
      isFinish.value = false
      finishTimer = null
    }, 600)

    if (props.showConfetti) {
      createConfetti()
    }
    emits('finish')
  } else {
    remainingMs.value = delta
  }
}

const stopTimer = () => {
  if (timer !== null) {
    clearInterval(timer)
    timer = null
  }
  isRunning.value = false
}

/** 生成礼花特效 */
const createConfetti = () => {
  if (!confettiContainer.value) return

  const container = confettiContainer.value
  const { width, height } = container.getBoundingClientRect()

  if (width <= 0 || height <= 0) return

  const confettiCount = 50
  const colors = [
    '#b24a2e',
    '#d3a437',
    '#4d956f',
    '#537bd0',
    '#9a4faf',
    '#5fa0aa',
  ]

  for (let i = 0; i < confettiCount; i++) {
    const confetti = document.createElement('div')
    const size = Math.random() * 10 + 4
    const duration = 2.8 + Math.random() * 0.4
    const delay = Math.random() * 0.2
    const centerX = width / 2
    const centerY = height / 2
    const angle = Math.random() * Math.PI * 2
    const distance = Math.min(width, height) * (0.2 + Math.random() * 0.45)
    const rotation = Math.random() * 360
    const rotateMid =
      rotation + (Math.random() > 0.5 ? 1 : -1) * (80 + Math.random() * 140)
    const rotateEnd =
      rotateMid + (Math.random() > 0.5 ? 1 : -1) * (180 + Math.random() * 240)
    const color = colors[Math.floor(Math.random() * colors.length)]
    const isStrip = Math.random() > 0.45
    const pieceWidth = isStrip ? size * 1.6 : size
    const pieceHeight = isStrip ? Math.max(3, size * 0.45) : size
    const opacity = 0.88 + Math.random() * 0.1
    const fromLeft = Math.random() > 0.5
    const launchX =
      width * (fromLeft ? 0.18 : 0.82) + (Math.random() - 0.5) * width * 0.08
    const launchY = height * 0.88 + (Math.random() - 0.5) * height * 0.06
    const centerTargetX =
      centerX - pieceWidth / 2 + (Math.random() - 0.5) * width * 0.08
    const centerTargetY =
      centerY - pieceHeight / 2 + (Math.random() - 0.5) * height * 0.08
    const toCenterX = centerTargetX - launchX
    const toCenterY = centerTargetY - launchY
    const explodeX = Math.cos(angle) * distance
    const explodeY = Math.sin(angle) * distance
    const finalX = toCenterX + explodeX
    const finalY = toCenterY + explodeY

    confetti.style.cssText = `
      position: absolute;
      left: ${launchX}px;
      top: ${launchY}px;
      width: ${pieceWidth}px;
      height: ${pieceHeight}px;
      background-color: ${color};
      border-radius: ${isStrip ? pieceHeight / 2 : 50}%;
      pointer-events: none;
      --ll-confetti-center-x: ${toCenterX}px;
      --ll-confetti-center-y: ${toCenterY}px;
      --ll-confetti-final-x: ${finalX}px;
      --ll-confetti-final-y: ${finalY}px;
      --ll-confetti-rotate-start: ${rotation}deg;
      --ll-confetti-rotate-mid: ${rotateMid}deg;
      --ll-confetti-rotate-end: ${rotateEnd}deg;
      animation: confetti-fall ${duration}s linear ${delay}s forwards;
      transform: rotate(${rotation}deg);
      opacity: ${opacity};
      box-shadow: 0 0 ${Math.max(4, size / 1.9)}px ${color};
    `

    container.appendChild(confetti)

    // 动画结束后清理
    setTimeout(
      () => {
        confetti.remove()
      },
      (duration + delay) * 1000,
    )
  }
}

const startTimer = () => {
  stopTimer()
  tick()
  isRunning.value = true

  const interval =
    props.precision > 0 ? Math.pow(10, 3 - props.precision) : 1000
  timer = setInterval(tick, interval)
}

/** 重置倒计时 */
const reset = () => {
  stopTimer()
  init()
}

/** 控制按钮：开始/暂停切换 */
const togglePlayPause = () => {
  if (isRunning.value) {
    pause()
  } else {
    start()
  }
}

/** 控制按钮：重置 */
const handleReset = () => {
  reset()
}

watch(
  () => props.active,
  (val) => {
    if (val) {
      // timer 模式：重新解析 lastTime，确保显示最新剩余时间
      // countdown 模式：基于暂停时存储的剩余时间恢复
      if (props.mode === 'timer') {
        targetTime.value = resolveTargetTime()
        remainingMs.value = Math.max(0, targetTime.value - Date.now())
      } else {
        targetTime.value = Date.now() + remainingMs.value
      }
      startTimer()
    } else {
      stopTimer()
    }
  },
)

// value 变化时自动重置并停止
watch(
  () => props.value,
  () => {
    stopTimer()
    init()
  },
)

onMounted(() => {
  init()
  if (props.active) {
    startTimer()
  }
})

onUnmounted(() => {
  stopTimer()
  if (finishTimer !== null) {
    clearTimeout(finishTimer)
  }
})

// 根据剩余时间和选中单位计算展示数据
const displayItems = computed(() => {
  const selected = new Set(props.units)
  const totalMs = remainingMs.value
  const totalSeconds = Math.floor(totalMs / 1000)
  const subMs = totalMs - totalSeconds * 1000 // 不足 1 秒的毫秒部分

  let remaining = totalSeconds
  const result: { key: TimeUnit; value: string; label: string }[] = []

  for (let i = 0; i < ALL_UNITS.length; i++) {
    const unit = ALL_UNITS[i]
    if (!selected.has(unit)) {
      // 未选中：跳过，剩余时间由下一个选中单位承接（自动向下合并）
      continue
    }

    const factor = UNIT_SECONDS[unit]
    const val = Math.floor(remaining / factor)
    remaining = remaining % factor

    // 最小单位 ss 且精度 > 0：追加毫秒小数部分
    if (unit === 'ss' && props.precision > 0) {
      const decimal = subMs / 1000
      const intPart = String(val).padStart(2, '0')
      const decPart = decimal.toFixed(props.precision).slice(1) // ".3" / ".32"
      result.push({
        key: unit,
        value: intPart + decPart,
        label: UNIT_LABELS[unit],
      })
    } else {
      const digits = 2
      result.push({
        key: unit,
        value: String(val).padStart(digits, '0'),
        label: UNIT_LABELS[unit],
      })
    }
  }

  return result
})

/** 开始/恢复倒计时 */
const start = () => {
  if (props.mode === 'timer') {
    targetTime.value = resolveTargetTime()
    remainingMs.value = Math.max(0, targetTime.value - Date.now())
  } else {
    targetTime.value = Date.now() + remainingMs.value
  }
  startTimer()
}

/** 暂停倒计时 */
const pause = () => {
  stopTimer()
}

defineExpose({ reset, start, pause, isRunning })
</script>
